
import {
  GuildGrade,
  type Guild,
  type GuildInvitation,
  type GuildMember,
  type GuildWarArena,
  type GuildWarMenu,
  type GuildComment,
  type GuildComments,
  type GuildLandState,
} from "../net/messages";
import { mmToLegacy } from "../sim/fixed";
import { formatYang, guildAmount } from "../money";
import { JOB_NAMES } from "./charselect";
import { elem, installChrome, makeDraggable, styleInstaller } from "./chrome";
import { yieldToCandidateWindow } from "./ime";
import { skillIconNode } from "./itemicon";
import type { QuickSkill } from "./quickslot";
import { skillDescFor } from "./skillbar";
import {
  DISABLE_COLOR,
  ENABLE_COLOR,
  GUILD_BUILDING_CATEGORIES,
  GUILD_BUILDINGS,
  GuildSiting,
  buildRefusal,
  buildingCost,
  buildingsOfCategory,
} from "./guildsiting";

/** One buildable rectangle, a `RECT` as `CMapOutdoor::RegisterGuildArea`
 *  stores it (`MapOutdoor.cpp:1446-1450`). */
export interface GuildArea {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

export interface GuildActions {
  /** Found a guild under this name. Everything else is the server's. */
  onFound?: (name: string) => void;
  /** Accept or decline, naming the token of the invitation on screen. */
  onAnswer?: (token: number, accept: boolean) => void;
  /** Walk out. Refused for the master. */
  onLeave?: () => void;
  /** Dissolve the guild. Master only, and the server decides that. */
  onDisband?: () => void;
  /** Rename one rank and set its four authority bits (Invite / Kick / Notice /
   *  Skill). A grade index and the row's full desired state, never the
   *  sender's own permission set -- see the file docstring. */
  onRank?: (
    grade: number,
    name: string,
    mayInvite: boolean,
    mayKick: boolean,
    mayNotice: boolean,
    maySkill: boolean,
  ) => void;
  /** Move the member in one roster row to another rank. The row's index and
   *  the revision of the roster that drew it, never a person or identity. */
  onPromote?: (slot: number, grade: number, rosterRevision: number) => void;
  /** Expel a member in one roster row from the roster revision that drew it. */
  onExpel?: (slot: number, rosterRevision: number) => void;
  /** Select or clear a member in one roster row from the frame that drew it. */
  onGeneral?: (slot: number, assign: boolean, rosterRevision: number) => void;
  /** Make a member in one rendered roster row the master. Irreversible. */
  onHandOver?: (slot: number, rosterRevision: number) => void;
  /** Put gold in, or take it out. The server clamps and refuses. */
  onBank?: (amount: number, withdraw: boolean) => void;
  /** Burn character experience to raise the guild's level. */
  onOffer?: (amount: number) => void;
  /** Restore shared Dragon Ghost. Requested points, never gold or a total. */
  onChargePower?: (amount: number) => void;
  /** The vnum selects one displayed row; all authority stays server-side. */
  onSkillUp?: (skill: number) => void;
  onUseSkill?: (skill: number) => void;
  /** Ctrl+click on a learned skill: the first empty quick slot
   *  (`RequestAddToEmptyLocalQuickSlot`, `uiguild.py:1920-1923`). */
  onAddQuickSkill?: (skill: number) => void;
  /** A skill dropped on the quick slot with this local index (`uiguild.py:1925`). */
  onBindQuickSlot?: (quickIndex: number, skill: number) => void;
  /** A picture chosen for the guild symbol (`__OnSelectSymbol`,
   *  `uiguild.py:1745-1747`); the shell checks it is 64x128 and sends it. */
  onPickSymbol?: (file: File) => void;
  /** Consume one drawn server-issued candidate; never a guild id, rule, or score. */
  onWarChallenge?: (token: number) => void;
  /** Refuse one drawn incoming declaration by its opaque candidate token. */
  onWarRefuse?: (token: number) => void;
  /** Post text; the server decides membership, author and notice authority. */
  onPostComment?: (content: string) => void;
  /** Delete one board row from the frame that drew it; authorization stays server-side. */
  onDeleteComment?: (slot: number, boardRevision: number) => void;
  /** Re-read the current server-owned board rows. */
  onRefreshComments?: () => void;
  onPlaceBuilding?: (vnum: number, x: number, y: number, rotation: number) => void;
  /** Where the character stands, which is where the building window opens
   *  (`player.GetMainCharacterPosition`, `root/uiguild.py:2304`). */
  sitingOrigin?: () => { readonly xMm: number; readonly yMm: number } | null;
}

/** `guild.LEADER_GRADE`. Grade 1 is the master and is the same fact as
 *  `Guild.master` -- see `guild.py`'s module docstring on why the original
 *  stores that twice. */
export const MASTER_GRADE = 1;

/** `guild.GRADE_COUNT`. Fifteen ranks exist from the moment a guild does
 *  (`guild.h:11`) -- there is no add-a-rank -- so the dropdowns below are a
 *  fixed range rather than a list built from what the server sent. */
export const GRADE_COUNT = 15;

// The seven shipped GUILD-group skill names verbatim (`skilldesc.txt:23-29`,
// vnums 151-157).
const GUILD_SKILLS: readonly [number, string, string][] = [
  // The icon column of the same rows (`metin3/data/skills.json`, `skill_proto`).
  [151, "Dragon Eyes", "yongan"], [152, "Blood of Dragon God", "gaho"],
  [153, "Benediction of Dragon God", "chukbok"], [154, "Holy Armour", "seonghwi"],
  [155, "Acceleration", "gasok"], [156, "Rage of Dragon God", "bunno"],
  [157, "Casting Aid", "jumunsul"],
];

/** Whether a vnum is a guild skill, which a quick slot casts through
 *  `UseGuildSkill` rather than the player's own skill list
 *  (`SKILL_TYPE_GUILD`, `PythonPlayerSkill.cpp:119-121`). */
export function isGuildSkill(vnum: number): boolean {
  return GUILD_SKILLS.some(([row]) => row === vnum);
}

/** Every guild skill as a quick slot paints it: name, icon and the guild's
 *  level for it, the facts `OnPickUpGuildSkill` attaches (`uiguild.py:1909-1929`). */
export function guildQuickSkills(guild: Guild): QuickSkill[] {
  return GUILD_SKILLS.map(([vnum, name, icon]) => ({
    vnum,
    name,
    icon,
    level: guild.guild_id === 0 ? 0 : guildSkillState(guild, vnum).level,
  }));
}

const GUILD_SKILL_FIELDS: Readonly<Record<number, readonly [keyof Guild, keyof Guild]>> = {
  151: ["dragon_eyes_level", "dragon_eyes_ready"],
  152: ["dragon_blood_level", "dragon_blood_ready"],
  153: ["dragon_benediction_level", "dragon_benediction_ready"],
  154: ["holy_armour_level", "holy_armour_ready"],
  155: ["acceleration_level", "acceleration_ready"],
  156: ["dragon_rage_level", "dragon_rage_ready"],
  157: ["casting_aid_level", "casting_aid_ready"],
};

function guildSkillState(guild: Guild, skill: number): { level: number; ready: number } {
  const fields = GUILD_SKILL_FIELDS[skill] ?? GUILD_SKILL_FIELDS[154];
  return { level: Number(guild[fields[0]]), ready: Number(guild[fields[1]]) };
}

/** `bMaxLevel` on every GUILD row of the shipped skill table, which the page
 *  reads back as `skill.GetSkillMaxLevel` to decide whether a skill may still
 *  be raised (`uiguild.py:1680-1690`). */
const GUILD_SKILL_MAX_LEVEL = 7;

/** The name, the level it stands at and its own description, the three things
 *  the original's skill slot says (`uiguild.py:1676-1682`, `:1982-1990`). */
export function guildSkillHoverText(name: string, vnum: number, level: number): string {
  const lines = [level <= 0 ? name : `${name} (level ${level})`];
  for (const line of skillDescFor(vnum) ?? []) {
    if (line !== "") lines.push(line);
  }
  return lines.join("\n");
}

type GuildTab = "GUILD_INFO" | "BOARD" | "MEMBER" | "BASE_INFO" | "SKILL" | "GRADE";

/** `guildwindow.py:13-14` — movable+float GuildWindow board size. */
/** `uiScriptLocale.GUILD_PATH` (`guildwindow.py:4`), where the tab strip is. */
const GUILD_TAB_ART = "/ui/textures/locale/guild";

/** `Dragon_God_Power_Gauge_Slot`'s own raster, 180x13
 *  (`guildwindow_guildskillpage.py:164-170`). */
const GUILD_GAUGE_ART = "/ui/textures/game/guild/gauge.png";
export const GUILD_GAUGE_WIDTH = 180;
export const GUILD_GAUGE_HEIGHT = 13;

/** `CommentSlot`'s own plate: `ui.MakeImageBox(self, "Parameter_Slot_06.sub",
 *  0, 0)` at (0,0), and the slot takes the picture's size
 *  (`uiguild.py:645-668`). 220x18. */
const GUILD_COMMENT_ART = "/ui/textures/public/parameter_slot_06.png";
export const GUILD_COMMENT_HEIGHT = 18;

/** `d:/ymir work/ui/game/guild/`, the guild window's own art. */
const GUILD_ART = "/ui/textures/game/guild";

/** The two folders the guild pages borrow a button from. */
const TASKBAR_ART = "/ui/textures/game/taskbar";
const PUBLIC_ART = "/ui/textures/public";

export const GUILD_WIDTH = 376;
export const GUILD_HEIGHT = 356;

/** `TabControl` band inside the board (`guildwindow.py:37-41`). */
export const GUILD_TAB_Y = 328;
export const GUILD_TAB_HEIGHT = 37;

export const GUILD_TABS: readonly {
  readonly id: GuildTab;
  readonly className: string;
  readonly x: number;
  readonly width: number;
  readonly height: number;
  readonly label: string;
}[] = [
  { id: "GUILD_INFO", className: "guild-tab-info", x: 6, width: 53, height: 27, label: "Guild Information" },
  { id: "BOARD", className: "guild-tab-board", x: 61, width: 67, height: 27, label: "Blackboard" },
  { id: "MEMBER", className: "guild-tab-member", x: 130, width: 60, height: 27, label: "Guild Members" },
  { id: "BASE_INFO", className: "guild-tab-baseinfo", x: 192, width: 60, height: 27, label: "Basic Information" },
  { id: "SKILL", className: "guild-tab-skill", x: 254, width: 60, height: 27, label: "Guild Skills" },
  { id: "GRADE", className: "guild-tab-grade", x: 316, width: 55, height: 27, label: "Rights Administration" },
];

export const GRADE_AUTH_COLUMNS = [
  { field: "may_invite", className: "guild-rank-invite", label: "Invite", bit: "Auth.ADD_MEMBER" },
  { field: "may_kick", className: "guild-rank-kick", label: "Kick", bit: "Auth.REMOVE_MEMBER" },
  { field: "may_notice", className: "guild-rank-notice", label: "Notice", bit: "Auth.NOTICE" },
  { field: "may_skill", className: "guild-rank-skill", label: "Skill", bit: "Auth.USE_SKILL" },
] as const;

const GRADE_AUTH_PROBE = new GuildGrade();

function gradeAuthOnWire(field: string): boolean {
  return field in GRADE_AUTH_PROBE;
}

function gradeAuthValue(row: GuildGrade | undefined, field: string): boolean {
  if (row === undefined) return false;
  return Boolean((row as unknown as Record<string, unknown>)[field]);
}

const BUILDING_NAME_BY_VNUM = new Map(GUILD_BUILDINGS.map((row) => [row.vnum, row.name]));

/** The catalog's own name for a placed building, or its bare vnum when the
 *  server sent a prototype this table does not carry -- never invented. */
function buildingName(vnum: number): string {
  return BUILDING_NAME_BY_VNUM.get(vnum) ?? `Building #${vnum}`;
}

export function formatInvitationExpiry(seconds: number): string {
  const remaining = Math.max(0, Math.trunc(seconds));
  return ` · ${remaining}s left`;
}

export const GRADE_NAME_MAX = 8;

/** `CPythonGuild::ENEMY_GUILD_SLOT_MAX_COUNT` (`PythonGuild.h:11`): the Guild
 *  Information page has six `EnemyGuildSlot` boxes
 *  (`guildwindow_guildinfopage.py:236-303`) and `RefreshGuildInfoPage` walks
 *  exactly that many (`uiguild.py:1497-1505`). */
export const ENEMY_GUILD_SLOTS = 6;

/** `GUILD_INFO_ENEMY_GUILD_EMPTY` (`locale_interface.txt:103`). The text
 *  `OnEndGuildWar` puts a slot back to, which is why it is a constant rather
 *  than an empty box (`uiguild.py:1967-1981`). */
export const ENEMY_GUILD_EMPTY = "None";

/** What founding costs, stated because it is the server's and a player who does
 *  not know it reads a refusal as a bug. Not enforced here. */
const GUILD_FOUNDING_COST = 200_000;

const STYLE_ID = "guild-style";

const STYLE = `
.guild {
  position: fixed;
  left: 12px;
  top: 120px;
  z-index: 58;
  width: ${GUILD_WIDTH}px;
  height: ${GUILD_HEIGHT}px;
  box-sizing: border-box;
  padding: 0;
  pointer-events: auto;
  overflow: hidden;
  color: #e8dcc0;
}
.guild[hidden] { display: none; }
.guild .m2-titlebar {
  width: 360px;
  box-sizing: border-box;
  pointer-events: auto;
  z-index: 4;
}
.guild-title {
  position: absolute;
  left: 12px;
  top: 32px;
  right: 12px;
  font-size: 0.66rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8fa0b4;
  display: flex;
  justify-content: space-between;
  gap: 8px;
}
.guild-name { color: #e8c877; font-weight: 600; text-transform: none; letter-spacing: 0; }
.guild-list { margin: 6px 0; max-height: 200px; overflow-y: auto; }
.guild-member {
  display: flex;
  align-items: baseline;
  gap: 6px;
  padding: 1px 0;
}
.guild-member-offline { opacity: 0.42; }
.guild-member-name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; }
.guild-member-master::before { content: "\\2605\\00a0"; color: #ffd27a; }
.guild-member-rank { color: #9fb6d4; font-size: 0.9em; }
.guild-member-level { color: #9fb6d4; }
.guild-member-job, .guild-member-offer { color: #9fb6d4; }
.guild-skill-slot {
  width: 32px; height: 32px; margin: 4px 0; cursor: grab; overflow: hidden;
  background: url("/ui/textures/public/slot_base.png") center / 32px 32px no-repeat;
}
.guild-skill-slot-unlearned { filter: grayscale(1); cursor: default; }
.guild-buttons { display: flex; gap: 6px; margin-top: 6px; }
.guild-button {
  flex: 1;
  padding: 4px 0;
  border: 1px solid rgba(232, 200, 119, 0.34);
  background: rgba(58, 47, 34, 0.9);
  color: #ead7ae;
  font: inherit;
  cursor: pointer;
}
.guild-button:hover:not(:disabled) { background: rgba(58, 47, 34, 1); border-color: rgba(232, 200, 119, 0.55); }
.guild-button:disabled { opacity: 0.4; cursor: default; }
/* RefreshButton on the base-information page
   (guildwindow_baseinfopage.py:179-190), 20x18 in three faces. */
/* CommentSlot is the plate, not a border: the row is drawn on
   Parameter_Slot_06 and sized by it (uiguild.py:645,668). The text sits two
   pixels in, left aligned, where slotSimpleText puts it (:649-656). */
.guild-comment {
  box-sizing: border-box;
  min-height: ${GUILD_COMMENT_HEIGHT}px;
  padding: 0 18px 0 2px;
  border: 0;
  background: url("${GUILD_COMMENT_ART}") 0 0 / 100% ${GUILD_COMMENT_HEIGHT}px no-repeat;
  image-rendering: pixelated;
}
.guild-comment-refresh {
  flex: none;
  width: 20px;
  height: 18px;
  padding: 0;
  border: 0;
  font-size: 0;
  color: transparent;
  background: url("${GUILD_ART}/refresh_button_01.png") no-repeat left top;
  image-rendering: pixelated;
}
.guild-comment-refresh:hover:not(:disabled) {
  background-image: url("${GUILD_ART}/refresh_button_02.png");
}
.guild-comment-refresh:active:not(:disabled) {
  background-image: url("${GUILD_ART}/refresh_button_03.png");
}
/* PostCommentButton (guildwindow_boardpage.py:44-46), 20x18 in three faces. */
.guild-comment-post {
  flex: none;
  width: 20px;
  height: 18px;
  padding: 0;
  border: 0;
  font-size: 0;
  color: transparent;
  background: url("${TASKBAR_ART}/send_chat_button_01.png") no-repeat left top;
  image-rendering: pixelated;
}
.guild-comment-post:hover:not(:disabled) {
  background-image: url("${TASKBAR_ART}/send_chat_button_02.png");
}
.guild-comment-post:active:not(:disabled) {
  background-image: url("${TASKBAR_ART}/send_chat_button_03.png");
}
/* PositionButton, the big button on the building form
   (buildguildbuildingwindow.py:212-214), 53x39. */
.guild-land-position {
  flex: none;
  width: 53px;
  height: 39px;
  padding: 0;
  border: 0;
  color: #ead7ae;
  font: 11px/39px Tahoma, "MS Sans Serif", sans-serif;
  background: url("${PUBLIC_ART}/big_button_01.png") no-repeat left top;
  image-rendering: pixelated;
}
.guild-land-position:hover:not(:disabled) {
  background-image: url("${PUBLIC_ART}/big_button_02.png");
}
.guild-land-position:active:not(:disabled),
.guild-land-preview-down {
  background-image: url("${PUBLIC_ART}/big_button_03.png");
}
.guild-found {
  position: absolute;
  left: 8px;
  top: 52px;
  width: 360px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  box-sizing: border-box;
}
.guild-found-input {
  padding: 5px 8px;
  border: 1px solid rgba(232, 200, 119, 0.22);
  background: rgba(0, 0, 0, 0.4);
  color: inherit;
  font: inherit;
}
.guild-hint { color: #9a8b6e; font-size: 0.9em; }

.guild-section {
  margin-top: 8px;
  padding-top: 6px;
}
/* Each group's heading sits on the horizontalbar the page gives it
   (guildwindow_baseinfopage.py:49-54, :134). */
.guild-section-title {
  font: 700 11px/17px Tahoma, "MS Sans Serif", sans-serif;
  letter-spacing: 0.04em;
  color: #e8c877;
  padding-left: 6px;
}
.guild-figure { display: flex; justify-content: space-between; gap: 8px; }
.guild-figure-value { color: #e8c877; }
.guild-row { display: flex; gap: 6px; margin-top: 4px; }
.guild-number {
  flex: 1;
  min-width: 0;
  padding: 4px 6px;
  border: 1px solid rgba(232, 200, 119, 0.22);
  background: rgba(0, 0, 0, 0.4);
  color: inherit;
  font: inherit;
}
.guild-select {
  padding: 3px 4px;
  border: 1px solid rgba(232, 200, 119, 0.22);
  background: rgba(0, 0, 0, 0.4);
  color: inherit;
  font: inherit;
}
.guild-check { display: flex; align-items: center; gap: 4px; color: #c9b896; font-size: 0.9em; }
.guild-check input:disabled { cursor: not-allowed; }
.guild-auth-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 6px;
}
.guild-member-grade { max-width: 84px; }
.guild-member-handover {
  padding: 0 5px;
  border: 1px solid rgba(255, 210, 122, 0.4);
  background: rgba(255, 210, 122, 0.1);
  color: #ffd27a;
  font: inherit;
  cursor: pointer;
}
.guild-member-expel {
  padding: 0 5px;
  border: 1px solid rgba(255, 126, 126, 0.4);
  background: rgba(255, 126, 126, 0.1);
  color: #ffaaaa;
  font: inherit;
  cursor: pointer;
}

/* Six EnemyGuildSlot boxes, 26 px apart, under the "Enemy Guild" bar
   (guildwindow_guildinfopage.py:228-303). */
.guild-enemies { display: flex; flex-direction: column; gap: 2px; }
.guild-enemy {
  height: 22px;
  line-height: 22px;
  text-align: center;
  background: rgba(0, 0, 0, 0.24);
  border: 1px solid rgba(255, 236, 170, 0.14);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.guild-enemy[data-guild-id] { color: #ffc8c8; }

/* Dragon_God_Power_Gauge_Slot is the empty track and Dragon_God_Power_Gauge
   the fill drawn over it at x + 4 (guildwindow_guildskillpage.py:164-178). */
.guild-skill-gauge {
  position: relative;
  width: ${GUILD_GAUGE_WIDTH}px;
  height: ${GUILD_GAUGE_HEIGHT}px;
  background: url("${GUILD_GAUGE_ART}") 0 0 / ${GUILD_GAUGE_WIDTH}px ${GUILD_GAUGE_HEIGHT}px no-repeat;
  image-rendering: pixelated;
}
.guild-skill-gauge-fill {
  position: absolute;
  left: 4px;
  top: 2px;
  height: ${GUILD_GAUGE_HEIGHT - 4}px;
  background: linear-gradient(180deg, #ffe6a4, #d79a2e);
}

.guild-tabs {
  position: absolute;
  left: 0;
  top: ${GUILD_TAB_Y}px;
  width: ${GUILD_WIDTH}px;
  height: ${GUILD_TAB_HEIGHT}px;
  background: no-repeat left top;
  image-rendering: pixelated;
}
/* Tab_01..Tab_06 (guildwindow.py:44-84): one 376x37 strip per selected page,
   shown one at a time, with the six buttons over it as hit areas. */
${GUILD_TABS.map((tab, index) =>
  `.guild-tabs[data-tab="${tab.id}"] {`
  + ` background-image: url("${GUILD_TAB_ART}/tab_${String(index + 1)}.png"); }`,
).join("\n")}
.guild-tab {
  position: absolute;
  top: 5px;
  height: 27px;
  padding: 0;
  border: 0;
  background: transparent;
  /* The strip carries the words; this is the button over it. */
  font-size: 0;
  color: transparent;
  cursor: pointer;
}
.guild-page {
  position: absolute;
  left: 8px;
  top: 52px;
  width: 360px;
  height: 270px;
  overflow-x: hidden;
  overflow-y: auto;
  box-sizing: border-box;
}
.guild-page[hidden] { display: none; }
.guild-page-member .guild-buttons { margin-top: 8px; }
.guild-land-status { margin-top: 4px; }
.guild-land-buildings { margin: 6px 0; }
.guild-land-building {
  justify-content: space-between;
  align-items: center;
}
.guild-land-building-label { flex: 1; min-width: 0; }
.guild-land-form { margin-top: 6px; }
.guild-land-form[hidden] { display: none; }
.guild-land-rot:disabled { opacity: 0.4; }

.guild-invite {
  position: fixed;
  left: 50%;
  top: 36%;
  transform: translateX(-50%);
  z-index: 63;
  padding: 12px 16px;
  border: 1px solid rgba(232, 200, 119, 0.34);
  background: rgba(12, 14, 18, 0.94);
  pointer-events: auto;
  font: 13px/1.5 Tahoma, "MS Sans Serif", sans-serif;
  color: #e8dcc0;
  text-align: center;
}
.guild-invite-buttons { display: flex; gap: 8px; margin-top: 8px; }
`;

const installStyle = styleInstaller(STYLE_ID, STYLE, installChrome);

export class GuildPanel {
  private readonly root: HTMLElement;
  private readonly nameLabel: HTMLElement;
  private readonly count: HTMLElement;
  private readonly list: HTMLElement;
  private readonly buttons: HTMLElement;
  private readonly leave: HTMLButtonElement;
  private readonly disband: HTMLButtonElement;
  private readonly found: HTMLElement;
  private readonly foundName: HTMLInputElement;

  /** The bank: what it holds, what to move, and the two directions. */
  private readonly bank: HTMLElement;
  private readonly bankGold: HTMLElement;
  private readonly bankAmount: HTMLInputElement;
  private readonly bankWithdraw: HTMLButtonElement;

  /** Contribution: the guild's own bar, this member's total, and the offer. */
  private readonly offer: HTMLElement;
  private readonly offerProgress: HTMLElement;
  private readonly offerMine: HTMLElement;
  private readonly offerAmount: HTMLInputElement;

  /** The bounded guild-skill slice.  These are only labels and requests; the
   *  Guild frame remains the source of power, permission and readiness. */
  private readonly skill: HTMLElement;
  private readonly skillPower: HTMLElement;
  private readonly skillGauge: HTMLElement;
  private readonly skillGaugeFill: HTMLElement;
  private readonly skillPoints: HTMLElement;
  private readonly skillLevel: HTMLElement;
  private readonly skillPick: HTMLSelectElement;
  private readonly skillLearn: HTMLButtonElement;
  private readonly skillUse: HTMLButtonElement;
  private readonly skillSlot: HTMLElement;
  private readonly symbolUpload: HTMLButtonElement;
  private readonly symbolFile: HTMLInputElement;
  private skillDrag: { pointerId: number; x: number; y: number } | null = null;
  private readonly skillChargeAmount: HTMLInputElement;
  private readonly skillCharge: HTMLButtonElement;
  private selectedSkill = 154;
  private currentGuild: Guild | null = null;
  private land: GuildLandState | null = null;
  private readonly buildableAreas: GuildArea[] = [];

  /** The six `uiguild.py` tabs (`Tab_01`..`Tab_06`, `uiguild.py:890-897`) as
   *  one page each, matching `SelectPage` (`uiguild.py:1422-1441`): every
   *  page but the active one is hidden, never stacked. */
  private readonly tabsBar: HTMLElement;
  private readonly tabButtons: Record<GuildTab, HTMLButtonElement>;
  private readonly pages: Record<GuildTab, HTMLElement>;
  private activeTab: GuildTab = "GUILD_INFO";

  /** The land/building page (`BASE_INFO`, `uiguild.py:1178-1215`). See the
   *  file's own note on `BuildGuildBuildingWindow` below for what this
   *  replaces and what it deliberately does not attempt. */
  private readonly landPanel: HTMLElement;
  private readonly landStatus: HTMLElement;
  private readonly landBuildings: HTMLElement;
  private readonly landForm: HTMLElement;
  private readonly landCategory: HTMLSelectElement;
  private readonly landBuildingPick: HTMLSelectElement;
  private readonly landX: HTMLElement;
  private readonly landY: HTMLElement;
  private readonly landRotation: HTMLInputElement[];
  private readonly landPreview: HTMLButtonElement;
  private readonly landCost: Record<"price" | "stone" | "log" | "plywood", HTMLElement>;
  private readonly landRefusal: HTMLElement;
  /** `BuildGuildBuildingWindow`'s state (`root/uiguild.py:2002-2627`). */
  readonly siting = new GuildSiting();
  private bagGold = 0;
  private bagCounts = new Map<number, number>();

  /** The rank editor. Master only, because renaming and re-authorising a rank
   *  are master-only in the original (`input_main.cpp:2327, 2353`) and a
   *  control everybody could press would be one everybody is refused. */
  private readonly ranks: HTMLElement;
  private readonly war: HTMLElement;
  private readonly warChoices: HTMLSelectElement;
  private readonly warButton: HTMLButtonElement;
  private readonly warRefuseButton: HTMLButtonElement;
  private readonly warBoard: HTMLElement;
  private readonly enemyGuilds: HTMLElement;
  private readonly comments: HTMLElement;
  private readonly commentList: HTMLElement;
  private readonly commentInput: HTMLInputElement;
  /** The opaque tokens from the exact server menu currently drawn. */
  private warChoiceTokens: number[] = [];
  private warChoiceIsAccept: boolean[] = [];
  private readonly rankPick: HTMLSelectElement;
  private readonly rankName: HTMLInputElement;
  private readonly rankAuth: Record<string, HTMLInputElement> = {};

  private readonly prompt: HTMLElement;
  private readonly promptText: HTMLElement;
  private readonly accept: HTMLButtonElement;
  private readonly decline: HTMLButtonElement;

  /** Whether the player has asked for the panel. A guild is not something that
   *  needs to be on screen while a fight is happening, unlike a party's health
   *  bars, so this one is opened rather than shown. */
  private open = false;

  /** A quest can ask for the original's standalone guild-name box even while
   * a roster is already current.  It is an event, not a claim that the client
   * may found a guild; the subsequent `GuildCreate` request is still refused
   * against the server's membership. */
  private foundingRequested = false;

  private drawn: GuildInvitation | null = null;

  /** The token this panel has already answered, so a second click on one prompt
   *  cannot become a second answer. -1 when it has answered nothing. */
  private answered = -1;

  /** The rank table as the server last sent it, so the editor can fill itself
   *  in when a different grade is picked. Never edited locally: the panel
   *  redraws from the next `Guild` frame like everything else here. */
  private grades: GuildGrade[] = [];

  /** The roster row a hand-over has been armed on, or -1. Giving a guild away
   *  is the second irreversible button in this client and it takes two clicks,
   *  which is the same argument that makes Leave and Disband two buttons: the
   *  server cannot tell a slip from an intention, so the panel asks. */
  private handingOver = -1;

  /** The roster row whose expulsion is armed, or -1. Removing somebody is
   *  persistent and the original gives it no undo, so it takes two clicks for
   *  the same reason handing the guild over does. */
  private expelling = -1;

  constructor(
    container: HTMLElement,
    private readonly actions: GuildActions = {},
  ) {
    const doc = container.ownerDocument;
    installStyle(doc);

    this.root = elem(doc, "div", "guild m2-board");
    this.root.hidden = true;
    this.root.setAttribute("role", "dialog");
    this.root.setAttribute("aria-label", "Guild");

    const titlebar = elem(doc, "div", "m2-titlebar", "Guild");
    const close = doc.createElement("button");
    close.type = "button";
    close.className = "m2-close";
    close.title = "Close";
    close.setAttribute("aria-label", "Close guild");
    close.addEventListener("click", () => {
      this.open = false;
      this.root.hidden = true;
    });
    makeDraggable(this.root, titlebar);

    const title = elem(doc, "div", "guild-title");
    this.nameLabel = elem(doc, "span", "guild-name");
    this.count = doc.createElement("span");
    title.append(this.nameLabel, this.count);

    this.list = elem(doc, "div", "guild-list");

    this.buttons = elem(doc, "div", "guild-buttons");
    this.leave = doc.createElement("button");
    this.leave.type = "button";
    this.leave.className = "guild-button guild-leave";
    this.leave.textContent = "Leave";
    this.leave.addEventListener("click", () => this.actions.onLeave?.());
    this.disband = doc.createElement("button");
    this.disband.type = "button";
    this.disband.className = "guild-button guild-disband";
    this.disband.textContent = "Disband";
    this.disband.addEventListener("click", () => this.actions.onDisband?.());
    this.buttons.append(this.leave, this.disband);

    this.found = elem(doc, "div", "guild-found");
    const hint = elem(doc, "div", "guild-hint");
    hint.textContent = `Founding costs ${formatYang(GUILD_FOUNDING_COST)} and needs level 40.`;
    this.foundName = doc.createElement("input");
    this.foundName.type = "text";
    this.foundName.className = "guild-found-input";
    this.foundName.placeholder = "Guild name";
    this.foundName.maxLength = 12;
    this.foundName.setAttribute("aria-label", "Guild name");
    this.foundName.autocomplete = "off";
    // The movement keys are letters; see `ui/chat.ts`. A panel that let its own
    // typing reach the window would walk the character while a name is typed.
    this.foundName.addEventListener("keydown", (event) => {
      event.stopPropagation();
      if (yieldToCandidateWindow(event)) return;
      if (event.key === "Enter") this.submitFound();
    });
    const foundButton = doc.createElement("button");
    foundButton.type = "button";
    foundButton.className = "guild-button guild-found-button";
    foundButton.textContent = "Found a guild";
    foundButton.addEventListener("click", () => this.submitFound());
    this.found.append(hint, this.foundName, foundButton);

    // ------------------------------------------------------------ the bank
    this.bank = elem(doc, "div", "guild-section guild-bank");
    const bankTitle = elem(doc, "div", "guild-section-title m2-hbar", "Bank");
    const bankFigure = elem(doc, "div", "guild-figure");
    const bankLabel = doc.createElement("span");
    bankLabel.textContent = "Holding";
    this.bankGold = elem(doc, "span", "guild-figure-value guild-bank-gold");
    bankFigure.append(bankLabel, this.bankGold);
    const bankRow = elem(doc, "div", "guild-row");
    this.bankAmount = this.number(doc, "guild-bank-amount", "Yang");
    // GUILD_DEPOSIT/GUILD_WITHDRAW (`locale_game.txt:169,201` -- "Deposit",
    // "Fallback"), the two money-dialog titles `uiguild.py:1762,1771` open for
    // exactly these two actions. "Fallback" reads oddly but is what shipped.
    const deposit = this.button(doc, "guild-bank-deposit", "Deposit", () =>
      this.submitBank(false),
    );
    this.bankWithdraw = this.button(doc, "guild-bank-withdraw", "Fallback", () =>
      this.submitBank(true),
    );
    bankRow.append(this.bankAmount, deposit, this.bankWithdraw);
    this.bank.append(bankTitle, bankFigure, bankRow);

    // --------------------------------------------------------- contribution
    this.offer = elem(doc, "div", "guild-section guild-offer");
    const offerTitle = elem(doc, "div", "guild-section-title m2-hbar");
    // GUILD_OFFER_EXP (`locale_game.txt:185` -- "Invest experience"), the
    // exact dialog title `uiguild.py:962` gives this action.
    offerTitle.textContent = "Invest experience";
    const offerBar = elem(doc, "div", "guild-figure");
    const offerBarLabel = doc.createElement("span");
    offerBarLabel.textContent = "Guild";
    this.offerProgress = elem(doc, "span", "guild-figure-value guild-offer-progress");
    offerBar.append(offerBarLabel, this.offerProgress);
    const offerMineRow = elem(doc, "div", "guild-figure");
    const offerMineLabel = doc.createElement("span");
    offerMineLabel.textContent = "Yours";
    this.offerMine = elem(doc, "span", "guild-figure-value guild-offer-mine");
    offerMineRow.append(offerMineLabel, this.offerMine);
    const offerRow = elem(doc, "div", "guild-row");
    this.offerAmount = this.number(doc, "guild-offer-amount", "Experience");
    const offerButton = this.button(doc, "guild-offer-button", "Give", () =>
      this.submitOffer(),
    );
    offerRow.append(this.offerAmount, offerButton);
    // Stated because it is the server's arithmetic and a player who does not
    // know it reads the result as a bug: a hundred of yours buys one of the
    // guild's (`guild.cpp:932-936`), and the request is floored to a multiple
    // of a hundred before anything is charged (`input_main.cpp:2378`).
    const offerHint = elem(
      doc,
      "div",
      "guild-hint",
      "100 of your experience buys 1 of the guild's.",
    );
    this.offer.append(offerTitle, offerBar, offerMineRow, offerRow, offerHint);

    // --------------------------------------------------------- guild skill
    this.skill = elem(doc, "div", "guild-section guild-skill");
    const skillTitle = elem(doc, "div", "guild-section-title m2-hbar");
    // GUILD_TILE_SKILL (`locale_game.txt:192` -- "Guild Skills"), the tab
    // this section corresponds to (`uiguild.py:886`).
    skillTitle.textContent = "Guild Skills";
    const skillFigure = elem(doc, "div", "guild-figure");
    const skillLabel = doc.createElement("span");
    skillLabel.textContent = "Dragon Ghost";
    this.skillPower = elem(doc, "span", "guild-figure-value guild-skill-power");
    skillFigure.append(skillLabel, this.skillPower);
    this.skillGauge = elem(doc, "div", "guild-skill-gauge");
    this.skillGaugeFill = elem(doc, "div", "guild-skill-gauge-fill");
    this.skillGauge.append(this.skillGaugeFill);
    const chargeRow = elem(doc, "div", "guild-row");
    this.skillChargeAmount = this.number(doc, "guild-skill-charge-amount", "Dragon Ghost");
    // GUILD_HEAL_GSP (`locale_game.txt:177` -- "Restore Dragon ghost"),
    // this exact action's shipped name (`uiguild.py:1882`).
    this.skillCharge = this.button(doc, "guild-skill-charge", "Restore Dragon ghost", () =>
      this.submitCharge(),
    );
    chargeRow.append(this.skillChargeAmount, this.skillCharge);
    const chargeHint = elem(doc, "div", "guild-hint", "100 Yang restores 1 Dragon Ghost.");
    // GUILD_SKILL_POINT (`locale_game.txt:191` -- "Skill Point"), the count
    // the original prints above the slots (`uiguild.py:1652-1653`).
    const pointsRow = elem(doc, "div", "guild-figure");
    const pointsLabel = doc.createElement("span");
    pointsLabel.textContent = "Skill Point";
    this.skillPoints = elem(doc, "span", "guild-figure-value guild-skill-points");
    pointsRow.append(pointsLabel, this.skillPoints);
    const levelRow = elem(doc, "div", "guild-figure");
    const levelLabel = doc.createElement("span");
    levelLabel.textContent = "Level";
    this.skillLevel = elem(doc, "span", "guild-figure-value guild-skill-level");
    levelRow.append(levelLabel, this.skillLevel);
    this.skillSlot = elem(doc, "div", "guild-skill-slot");
    this.skillSlot.addEventListener("click", (event) => {
      if (event.ctrlKey && this.selectedLevel() > 0) this.actions.onAddQuickSkill?.(this.selectedSkill);
    });
    this.skillSlot.addEventListener("pointerdown", (event) => {
      if (event.button !== 0 || this.selectedLevel() <= 0) return;
      this.skillDrag = { pointerId: event.pointerId, x: event.clientX, y: event.clientY };
    });
    this.skillSlot.addEventListener("pointerup", (event) => {
      const drag = this.skillDrag;
      this.skillDrag = null;
      if (drag === null || drag.pointerId !== event.pointerId) return;
      if (Math.hypot(event.clientX - drag.x, event.clientY - drag.y) <= 4) return;
      const under = doc.elementFromPoint?.(event.clientX, event.clientY);
      if (under) this.dropSkillOn(under);
    });
    this.skillPick = elem(doc, "select", "guild-select guild-skill-pick");
    this.skillPick.setAttribute("aria-label", "Guild skill");
    for (const [vnum, name] of GUILD_SKILLS) {
      const option = doc.createElement("option");
      option.value = String(vnum);
      option.textContent = name;
      this.skillPick.append(option);
    }
    this.skillPick.value = String(this.selectedSkill);
    this.skillPick.addEventListener("change", () => {
      this.selectedSkill = Number(this.skillPick.value);
      if (this.currentGuild !== null) this.update(this.currentGuild);
    });
    const skillRow = elem(doc, "div", "guild-row");
    this.skillLearn = this.button(doc, "guild-skill-learn", "Learn", () =>
      this.actions.onSkillUp?.(this.selectedSkill),
    );
    this.skillUse = this.button(doc, "guild-skill-use", "Use", () =>
      this.actions.onUseSkill?.(this.selectedSkill),
    );
    skillRow.append(this.skillLearn, this.skillUse);
    this.skill.append(skillTitle, skillFigure, this.skillGauge, chargeRow, chargeHint,
      pointsRow, levelRow, this.skillSlot, this.skillPick, skillRow);

    // ----------------------------------------------------------- the ranks
    this.ranks = elem(doc, "div", "guild-section guild-ranks");
    const ranksTitle = elem(doc, "div", "guild-section-title m2-hbar");
    // GUILD_TILE_GRADE (`locale_game.txt:189` -- "Rights Administration"),
    // the rank/permission-editing tab this section is (`uiguild.py:887`).
    ranksTitle.textContent = "Rights Administration";
    this.rankPick = elem(doc, "select", "guild-select guild-rank-pick");
    this.rankPick.setAttribute("aria-label", "Rank");
    // Grades 2..15. One is missing on purpose: the master's rank cannot be
    // renamed (`guild.cpp:785`) or re-authorised (`:833`), so offering it would
    // be offering a refusal.
    for (let grade = 2; grade <= GRADE_COUNT; grade += 1) {
      const option = doc.createElement("option");
      option.value = String(grade);
      option.textContent = String(grade);
      this.rankPick.append(option);
    }
    this.rankPick.addEventListener("change", () => this.fillRank());
    const rankRow = elem(doc, "div", "guild-row");
    this.rankName = this.number(doc, "guild-rank-name", "Rank name");
    this.rankName.type = "text";
    this.rankName.maxLength = GRADE_NAME_MAX;
    const rankSave = this.button(doc, "guild-rank-save", "Set", () =>
      this.submitRank(),
    );
    rankRow.append(this.rankPick, this.rankName, rankSave);
    const authRow = elem(doc, "div", "guild-auth-row");
    for (const col of GRADE_AUTH_COLUMNS) {
      const label = elem(doc, "label", "guild-check");
      const box = doc.createElement("input");
      box.type = "checkbox";
      box.className = col.className;
      const onWire = gradeAuthOnWire(col.field);
      box.disabled = !onWire;
      if (!onWire) {
        box.title = `${col.bit} is not on the GuildGrade wire`;
      }
      const text = doc.createElement("span");
      text.textContent = col.label;
      label.append(box, text);
      authRow.append(label);
      this.rankAuth[col.field] = box;
    }
    this.ranks.append(ranksTitle, rankRow, authRow);

    this.war = elem(doc, "div", "guild-section guild-war");
    const warTitle = elem(doc, "div", "guild-section-title m2-hbar", "Guild war");
    this.warChoices = elem(doc, "select", "guild-select guild-war-choice");
    this.warChoices.setAttribute("aria-label", "Guild war challenge");
    this.warChoices.addEventListener("change", () => this.updateWarButton());
    this.warButton = this.button(doc, "guild-war-challenge", "Challenge", () => {
      const token = this.warChoiceTokens[this.warChoices.selectedIndex];
      if (token !== undefined) this.actions.onWarChallenge?.(token);
    });
    this.warRefuseButton = this.button(doc, "guild-war-refuse", "Refuse", () => {
      const token = this.warChoiceTokens[this.warChoices.selectedIndex];
      if (token !== undefined) this.actions.onWarRefuse?.(token);
    });
    this.warBoard = elem(doc, "div", "guild-hint guild-war-board");
    // `Guild_Mark_Title` reads "Enemy Guild" (`locale_interface.txt:102`) over
    // six `XLARGE_VALUE_FILE` boxes 26 px apart
    // (`guildwindow_guildinfopage.py:228-303`).
    const enemyTitle = elem(doc, "div", "guild-section-title m2-hbar", "Enemy Guild");
    this.enemyGuilds = elem(doc, "div", "guild-enemies");
    for (let slot = 0; slot < ENEMY_GUILD_SLOTS; slot += 1) {
      const box = elem(doc, "div", "guild-enemy", ENEMY_GUILD_EMPTY);
      box.dataset.slot = String(slot + 1);
      this.enemyGuilds.append(box);
    }
    this.war.append(
      warTitle, this.warChoices, this.warButton, this.warRefuseButton, this.warBoard,
      enemyTitle, this.enemyGuilds,
    );

    this.landPanel = elem(doc, "div", "guild-section guild-land");
    const landTitle = elem(doc, "div", "guild-section-title m2-hbar", "Guild land");
    this.landStatus = elem(doc, "div", "guild-hint guild-land-status");
    this.landBuildings = elem(doc, "div", "guild-land-buildings");
    this.landForm = elem(doc, "div", "guild-land-form");
    // `CategoryList` and `BuildingList` (`buildguildbuildingwindow.py:48-192`).
    this.landCategory = elem(doc, "select", "guild-select guild-land-category");
    this.landCategory.setAttribute("aria-label", "Building Units");
    for (const [type, label] of GUILD_BUILDING_CATEGORIES) {
      const option = doc.createElement("option");
      option.value = type;
      option.textContent = label;
      this.landCategory.append(option);
    }
    this.landCategory.addEventListener("change", () => this.fillBuildingList());
    this.landBuildingPick = elem(doc, "select", "guild-select guild-land-building-pick");
    this.landBuildingPick.setAttribute("aria-label", "Building List");
    this.landBuildingPick.addEventListener("change", () => this.selectBuilding());
    const landPosRow = elem(doc, "div", "guild-row guild-land-position-row");
    // `BuildingPositionXValue`/`YValue` are read-outs, never fields
    // (`buildguildbuildingwindow.py:217-243`).
    this.landX = elem(doc, "span", "guild-land-x");
    this.landY = elem(doc, "span", "guild-land-y");
    const landPosition = this.button(doc, "guild-land-position", "Change", () => this.beginPositioning());
    landPosRow.append(elem(doc, "span", "guild-hint", "X"), this.landX, elem(doc, "span", "guild-hint", "Y"), this.landY, landPosition);
    // `BuildingRotationX/Y/Z` sliders (`buildguildbuildingwindow.py:260-289`).
    const landRotRow = elem(doc, "div", "guild-row guild-land-rotation-row");
    this.landRotation = ["x", "y", "z"].map((axis) => {
      const slider = doc.createElement("input");
      slider.type = "range";
      slider.min = "0";
      slider.max = "1";
      slider.step = "any";
      slider.value = "0.5";
      slider.className = `guild-land-rot guild-land-rot-${axis}`;
      slider.setAttribute("aria-label", `Orientation ${axis.toUpperCase()}`);
      landRotRow.append(slider);
      return slider;
    });
    this.landRotation[2].addEventListener("input", () => {
      this.siting.setSlider(Number(this.landRotation[2].value));
    });
    this.landPreview = this.button(doc, "guild-land-preview", "Preview", () => this.togglePreview());
    // Price and the three materials (`buildguildbuildingwindow.py:73-135`).
    const landCostRow = elem(doc, "div", "guild-land-cost");
    const costCell = (key: string, label: string): HTMLElement => {
      const value = elem(doc, "span", `guild-land-${key}`);
      const row = elem(doc, "div", "guild-row");
      row.append(elem(doc, "span", "guild-hint", label), value);
      landCostRow.append(row);
      return value;
    };
    this.landCost = {
      price: costCell("price", "Price"), // locale_interface.txt:82
      stone: costCell("stone", "Stone"), // locale_interface.txt:84
      log: costCell("log", "Log"), // locale_interface.txt:76
      plywood: costCell("plywood", "Plywood"), // locale_interface.txt:79
    };
    this.landRefusal = elem(doc, "div", "guild-hint guild-land-refusal");
    const landActions = elem(doc, "div", "guild-row");
    const landBuildButton = this.button(doc, "guild-land-build", "Accept", () => this.submitPlaceBuilding());
    const landCancel = this.button(doc, "guild-land-cancel", "Cancel", () => this.closeSiting());
    landActions.append(landBuildButton, landCancel);
    this.landForm.append(
      this.landCategory, this.landBuildingPick, landPosRow, landRotRow, this.landPreview,
      landCostRow, this.landRefusal, landActions,
    );
    this.landForm.hidden = true;
    this.landPanel.append(landTitle, this.landStatus, this.landBuildings, this.landForm);

    this.comments = elem(doc, "div", "guild-section guild-comments");
    const commentsTitle = elem(doc, "div", "guild-section-title m2-hbar");
    // GUILD_TILE_BOARD (`locale_game.txt:188` -- "Blackboard"), the tab this
    // section is (`uiguild.py:883`).
    commentsTitle.textContent = "Blackboard";
    const commentsRefresh = this.button(doc, "guild-comment-refresh", "Refresh", () =>
      this.actions.onRefreshComments?.(),
    );
    this.commentList = elem(doc, "div", "guild-comments-list");
    const commentRow = elem(doc, "div", "guild-row");
    this.commentInput = doc.createElement("input");
    this.commentInput.type = "text";
    this.commentInput.className = "guild-number guild-comment-input";
    this.commentInput.maxLength = 50;
    this.commentInput.placeholder = "Message ( !notice for officers )";
    this.commentInput.setAttribute("aria-label", "Guild board message");
    this.commentInput.addEventListener("keydown", (event) => {
      event.stopPropagation();
      if (yieldToCandidateWindow(event)) return;
      if (event.key === "Enter") this.submitComment();
    });
    // GUILD_COMMENT (`locale_game.txt:165` -- "Write"), this exact button's
    // shipped word (`uiguild.py:1083-1085`).
    const commentPost = this.button(doc, "guild-comment-post", "Write", () => this.submitComment());
    commentRow.append(this.commentInput, commentPost);
    this.comments.append(commentsTitle, commentsRefresh, this.commentList, commentRow);

    // -------------------------------------------------------------- tabs
    // One page visible at a time, matching `SelectPage` (`uiguild.py:1422-
    // 1441`). Buttons sit at `Tab_Button_01`..`06` coordinates, over the
    // strip whose raster says which of them is down.
    this.tabsBar = elem(doc, "div", "guild-tabs");
    this.tabsBar.setAttribute("role", "tablist");
    const tabButtons: Partial<Record<GuildTab, HTMLButtonElement>> = {};
    for (const tab of GUILD_TABS) {
      const tabButton = doc.createElement("button");
      tabButton.type = "button";
      tabButton.className = `guild-tab ${tab.className}`;
      tabButton.textContent = tab.label;
      tabButton.title = tab.label;
      tabButton.style.left = `${tab.x}px`;
      tabButton.style.width = `${tab.width}px`;
      tabButton.setAttribute("role", "tab");
      tabButton.addEventListener("click", () => this.selectTab(tab.id));
      this.tabsBar.append(tabButton);
      tabButtons[tab.id] = tabButton;
    }
    this.tabButtons = tabButtons as Record<GuildTab, HTMLButtonElement>;

    // The symbol button opens a file picker where `symbolSelectDialog` lists
    // the upload directory (`uiguild.py:1724-1729`, `uiuploadmark.py:236-316`).
    this.symbolFile = doc.createElement("input");
    this.symbolFile.type = "file";
    this.symbolFile.accept = "image/*";
    this.symbolFile.className = "guild-symbol-file";
    this.symbolFile.hidden = true;
    this.symbolFile.addEventListener("change", () => {
      const file = this.symbolFile.files?.[0];
      if (file !== undefined) this.actions.onPickSymbol?.(file);
    });
    this.symbolUpload = this.button(doc, "guild-symbol-upload", "Upload Symbol", () =>
      this.symbolFile.click(),
    );
    const symbolRow = elem(doc, "div", "guild-row guild-symbol");
    symbolRow.append(this.symbolUpload, this.symbolFile);

    const pageInfo = elem(doc, "div", "guild-page guild-page-info");
    pageInfo.append(this.bank, this.offer, symbolRow, this.war);

    const pageBoard = elem(doc, "div", "guild-page guild-page-board");
    pageBoard.append(this.comments);

    const pageMember = elem(doc, "div", "guild-page guild-page-member");
    pageMember.append(this.list, this.buttons);

    const pageBaseInfo = elem(doc, "div", "guild-page guild-page-baseinfo");
    pageBaseInfo.append(this.landPanel);

    const pageSkill = elem(doc, "div", "guild-page guild-page-skill");
    pageSkill.append(this.skill);

    const pageGrade = elem(doc, "div", "guild-page guild-page-grade");
    pageGrade.append(this.ranks);

    this.pages = {
      GUILD_INFO: pageInfo,
      BOARD: pageBoard,
      MEMBER: pageMember,
      BASE_INFO: pageBaseInfo,
      SKILL: pageSkill,
      GRADE: pageGrade,
    };

    // BoardWithTitleBar's close sits at the board's top right (root/ui.py:1570-1575),
    // where every other m2-board panel puts `.m2-close`.
    this.root.append(
      titlebar,
      close,
      title,
      this.tabsBar,
      pageInfo,
      pageBoard,
      pageMember,
      pageBaseInfo,
      pageSkill,
      pageGrade,
      this.found,
    );
    this.renderLand();

    this.prompt = elem(doc, "div", "guild-invite");
    this.prompt.hidden = true;
    this.promptText = elem(doc, "div", "guild-invite-text");
    const promptButtons = elem(doc, "div", "guild-invite-buttons");
    this.accept = doc.createElement("button");
    this.accept.type = "button";
    this.accept.className = "guild-button guild-invite-accept";
    this.accept.textContent = "Join";
    this.accept.addEventListener("click", () => this.answer(true));
    this.decline = doc.createElement("button");
    this.decline.type = "button";
    this.decline.className = "guild-button guild-invite-decline";
    this.decline.textContent = "Decline";
    this.decline.addEventListener("click", () => this.answer(false));
    promptButtons.append(this.accept, this.decline);
    this.prompt.append(this.promptText, promptButtons);

    container.append(this.root, this.prompt);
    // `GuildWindow.OnPressEscapeKey` closes the guild window and consumes
    // Escape (`uiguild.py:1997-1999`). Scope this to the visible roster window;
    // an invitation prompt has its own answer controls and is not the roster.
    const onEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || !this.showing) return;
      event.preventDefault();
      event.stopPropagation();
      this.toggle();
    };
    this.root.addEventListener("keydown", onEscape);
    doc.addEventListener("keydown", (event) => {
      if (!this.root.isConnected) return;
      onEscape(event);
    }, { capture: true });
  }

  /** The guild's level for the skill the page shows; zero with no guild. */
  private selectedLevel(): number {
    return this.currentGuild === null ? 0 : guildSkillState(this.currentGuild, this.selectedSkill).level;
  }

  /** Bind the shown skill to the quick slot `target` is inside, when it is one
   *  and the skill is learned (`OnPickUpGuildSkill`, `uiguild.py:1909-1929`). */
  dropSkillOn(target: Element): void {
    const cell = target.closest<HTMLElement>("[data-quick-index]");
    if (cell === null || this.selectedLevel() <= 0) return;
    const index = Number(cell.dataset.quickIndex);
    if (!Number.isInteger(index) || index < 0) return;
    this.actions.onBindQuickSlot?.(index, this.selectedSkill);
  }

  /** Show one tab's page and hide the rest, matching `SelectPage`
   *  (`uiguild.py:1422-1441`). */
  private selectTab(tab: GuildTab): void {
    this.activeTab = tab;
    this.applyTabs();
  }

  private applyTabs(): void {
    const inGuild =
      this.currentGuild !== null && this.currentGuild.guild_id !== 0 && !this.foundingRequested;
    // `GuildWindow.__LoadWindow` builds all six `tabButtonDict` entries and
    // wires every one to `SelectPage` (`uiguild.py:945-948`); only BASE_INFO
    // (always) and SKILL (`DISABLE_GUILD_SKILL`) are ever `Disable()`d there
    // (`:944, 947-948`), and `SelectPage` itself (`:1422-1441`) never reads
    // the local player's grade. The GRADE tab is shown to every member; what
    // a non-master may do inside it is a server-side refusal
    // (`CG_CHANGE_GRADE_AUTHORITY`/`_NAME`, `input_main.cpp:2317-2365`), not
    // a client-side hide.
    this.tabsBar.hidden = !inGuild;
    if (!inGuild) {
      for (const page of Object.values(this.pages)) page.hidden = true;
      return;
    }
    this.tabsBar.dataset.tab = this.activeTab;
    for (const key of Object.keys(this.pages) as GuildTab[]) {
      this.pages[key].hidden = key !== this.activeTab;
      this.tabButtons[key].classList.toggle("guild-tab-active", key === this.activeTab);
    }
  }

  get showing(): boolean {
    return !this.root.hidden;
  }

  /** The rectangles, in the land's own units, where this player's guild may
   *  build: `CMapOutdoor::m_rkList_kGuildArea` (`MapOutdoor.h:571`), filled by
   *  `RecvLandPacket` through `CPythonBackground::RegisterGuildArea`. */
  get guildAreas(): readonly GuildArea[] {
    return this.buildableAreas;
  }

  /** `CMapOutdoor::ClearGuildArea` (`MapOutdoor.cpp:1439-1442`). */
  clearGuildArea(): void {
    this.buildableAreas.length = 0;
  }

  /** `CMapOutdoor::RegisterGuildArea` (`MapOutdoor.cpp:1444-1452`). */
  registerGuildArea(isx: number, isy: number, iex: number, iey: number): void {
    this.buildableAreas.push({ left: isx, top: isy, right: iex, bottom: iey });
  }

  /** Keep the server's land rows solely to turn a displayed building into a
   * revision-bound action; no object id crosses the request boundary. */
  updateLand(state: GuildLandState): void {
    this.land = state;
    this.renderLand();
  }

  /** Redraw the land status line and the built-object table from the last
   *  `GuildLandState` frame. Never invents a plot or an object: `land_id`
   *  zero is the server's own "no plot" (`net/messages.ts`'s `GuildLandState`
   *  doc), and every row comes straight from `land.objects`. */
  private renderLand(): void {
    const doc = this.root.ownerDocument;
    const land = this.land;
    if (land === null || land.land_id === 0) {
      this.closeSiting();
      this.landStatus.textContent = "Your guild owns no land. Stand on a vacant plot to buy it.";
      this.landBuildings.replaceChildren();
      return;
    }
    this.landStatus.textContent = `Land #${land.land_id} on map ${land.map_index}`;
    this.landBuildings.replaceChildren(
      // No row carries an upgrade: a building changes only through its own
      // quest's `building.reconstruct` (`questlua_building.cpp:126-150`).
      ...land.objects.map((building) => {
        const row = elem(doc, "div", "guild-row guild-land-building");
        const label = elem(doc, "span", "guild-land-building-label");
        label.textContent =
          `${buildingName(building.vnum)} · (${building.x}, ${building.y}) · ${building.rotation}°`;
        row.append(label);
        return row;
      }),
    );
  }

  /** The bag the cost is printed against: gold and every carried stack,
   *  summed by vnum (`player.GetItemCountByVnum`). */
  setBag(bag: { readonly gold: number; readonly slots: readonly { readonly vnum: number; readonly count: number }[] }): void {
    this.bagGold = bag.gold;
    this.bagCounts = new Map();
    for (const held of bag.slots) this.bagCounts.set(held.vnum, (this.bagCounts.get(held.vnum) ?? 0) + held.count);
    this.refreshSiting();
  }

  /** `BUILD_OnUpdate`'s pick while positioning (`root/interfacemodule.py:1603-1610`). */
  sitingPoint(xMm: number, yMm: number): void {
    this.siting.pointAt(xMm, yMm);
    this.refreshSiting();
  }

  /** A click on the world while the window is open: true when it is spent
   *  here (`BUILD_OnMouseLeftButtonDown`, `root/interfacemodule.py:1614-1627`).
   *  Settling shows the window again (`SettleCurrentPosition`,
   *  `root/uiguild.py:2587-2599`). */
  sitingClick(): boolean {
    const spent = this.siting.click(this.buildableAreas);
    if (spent && this.siting.mode === "view") this.root.hidden = !this.open;
    this.refreshSiting();
    return spent;
  }

  /** Whether the BUILD game button shows here: the master, standing in one
   *  of his own guild's plots (`BULID_EnterGuildArea`,
   *  `root/interfacemodule.py:1630-1650`; `BULID_ExitGuildArea`, `:1652-1653`). */
  mayBuildAt(xMm: number, yMm: number): boolean {
    const guild = this.currentGuild;
    if (guild === null || guild.guild_id === 0 || guild.your_grade !== MASTER_GRADE) return false;
    const x = mmToLegacy(xMm);
    const y = mmToLegacy(yMm);
    return this.buildableAreas.some((area) => x >= area.left && y >= area.top && x <= area.right && y <= area.bottom);
  }

  /** The BUILD game button (`__OnClickBuildButton` -> `BUILD_OpenWindow`,
   *  `root/interfacemodule.py:1079-1080,1595-1599`): the building window,
   *  opened where the character stands. */
  openBuilding(): void {
    if (this.siting.isOpen) return;
    this.open = true;
    this.root.hidden = false;
    this.selectTab("BASE_INFO");
    this.openSiting();
  }

  private openSiting(): void {
    const origin = this.actions.sitingOrigin?.() ?? null;
    if (origin === null) return;
    this.siting.open(origin.xMm, origin.yMm);
    this.landCategory.value = GUILD_BUILDING_CATEGORIES[0][0];
    this.fillBuildingList();
    this.landForm.hidden = false;
  }

  /** `Close` (`root/uiguild.py:2323-2334`). */
  private closeSiting(): void {
    this.siting.close();
    this.landForm.hidden = true;
    this.landPreview.classList.remove("guild-land-preview-down");
    this.landRefusal.textContent = "";
    if (this.open) this.root.hidden = false;
  }

  /** `__RefreshItem` then `SelectItem(0)` (`root/uiguild.py:2557-2586`). */
  private fillBuildingList(): void {
    const doc = this.root.ownerDocument;
    this.landBuildingPick.replaceChildren(
      ...buildingsOfCategory(this.landCategory.value).map((row) => {
        const option = doc.createElement("option");
        option.value = String(row.vnum);
        option.textContent = row.name;
        return option;
      }),
    );
    this.selectBuilding();
  }

  private selectBuilding(): void {
    this.siting.select(Number(this.landBuildingPick.value));
    for (const slider of this.landRotation) slider.value = "0.5";
    this.refreshSiting();
  }

  private beginPositioning(): void {
    if (!this.siting.beginPositioning()) return;
    this.root.hidden = true;
  }

  /** `PreviewButton`'s toggle (`root/uiguild.py:2392-2411`). */
  private togglePreview(): void {
    if (this.siting.mode === "preview") this.siting.leavePreview();
    else this.siting.enterPreview();
    this.landPreview.classList.toggle("guild-land-preview-down", this.siting.mode === "preview");
  }

  /** `__SetBuildingData` and `SetBuildingPosition`'s read-outs
   *  (`root/uiguild.py:2457-2507,2601-2605`). */
  private refreshSiting(): void {
    const row = this.siting.building;
    const { x, y } = this.siting.position;
    this.landX.textContent = String(x);
    this.landY.textContent = String(y);
    if (row === null) return;
    const cost = buildingCost(row, this.bagGold, (vnum) => this.bagCounts.get(vnum) ?? 0);
    const paint = (cell: HTMLElement, text: string, enough: boolean): void => {
      cell.textContent = text;
      cell.style.color = enough ? ENABLE_COLOR : DISABLE_COLOR;
    };
    paint(this.landCost.price, guildAmount(cost.price), cost.enoughMoney);
    paint(this.landCost.stone, String(cost.stone), cost.enoughStone);
    paint(this.landCost.log, String(cost.log), cost.enoughLog);
    paint(this.landCost.plywood, String(cost.plywood), cost.enoughPlywood);
    // `if 0 == self.rot_*_limit: Disable()` (`root/uiguild.py:2501-2506`).
    this.landRotation.forEach((slider, axis) => {
      slider.disabled = row.rotLimit[axis] === 0;
    });
  }

  /** `Build` (`root/uiguild.py:2275-2301`): refused against the bag with the
   *  popup's text, else one `GuildPlaceBuilding` and the window closes. */
  private submitPlaceBuilding(): void {
    const row = this.siting.building;
    const placement = this.siting.placement;
    if (row === null || placement === null) return;
    const refusal = buildRefusal(buildingCost(row, this.bagGold, (vnum) => this.bagCounts.get(vnum) ?? 0));
    if (refusal !== null) {
      this.landRefusal.textContent = refusal;
      return;
    }
    this.actions.onPlaceBuilding?.(placement.vnum, placement.x, placement.y, placement.rotation);
    this.closeSiting();
  }

  get asking(): boolean {
    return !this.prompt.hidden;
  }

  /** The token Accept would send. -1 when no prompt is drawn. For tests. */
  get shownToken(): number {
    return this.drawn === null ? -1 : this.drawn.token;
  }

  get typing(): boolean {
    const focused = this.root.ownerDocument.activeElement;
    return (
      focused === this.foundName ||
      focused === this.bankAmount ||
      focused === this.offerAmount ||
      focused === this.rankName
      || focused === this.commentInput
    );
  }

  toggle(): void {
    this.open = !this.open;
    this.root.hidden = !this.open;
    if (!this.open && this.siting.isOpen) this.closeSiting();
  }

  /** `HEADER_GC_REQUEST_MAKE_GUILD` (`questlua_game.cpp:37-47`) is an open,
   * not a toggle.  Receiving it twice must leave the form open rather than
   * closing it, and it may deliberately cover an already-drawn roster like
   * the original `AskGuildName` popup. */
  requestFounding(): void {
    this.foundingRequested = true;
    this.open = true;
    this.root.hidden = false;
    if (this.currentGuild !== null) this.update(this.currentGuild);
    this.foundName.focus();
  }

  update(guild: Guild): void {
    this.currentGuild = guild;
    const doc = this.root.ownerDocument;
    const inGuild = guild.guild_id !== 0 && !this.foundingRequested;

    const master = guild.your_grade === MASTER_GRADE;

    this.found.hidden = inGuild;
    this.buttons.hidden = !inGuild;
    this.list.hidden = !inGuild;
    this.bank.hidden = !inGuild;
    this.offer.hidden = !inGuild;
    // MainPlayerHasAuthority(AUTH_NOTICE) (`uiguild.py:1724-1727`).
    this.symbolUpload.hidden =
      !inGuild || !(guild.grades[guild.your_grade - 1]?.may_notice ?? master);
    this.skill.hidden = !inGuild;
    // `__MakeGradePage` (`uiguild.py:1273-1330`) never checks the local
    // player's grade, so every member sees the rank editor; renaming and
    // re-authorising a rank are refused for a non-master only when they are
    // actually sent (`input_main.cpp:2317-2344`, `:2346-2366`).
    this.ranks.hidden = !inGuild;
    this.war.hidden = !inGuild;
    this.comments.hidden = !inGuild;
    this.applyTabs();

    if (!inGuild) {
      // `SELECT_NO_GUILD` (`locale_interface.txt:291`).
      this.nameLabel.textContent = "No Guild";
      this.count.textContent = "";
      this.list.replaceChildren();
      this.commentList.replaceChildren();
      this.grades = [];
      this.handingOver = -1;
      this.expelling = -1;
      return;
    }

    // Every number below is the server's copy. Nothing here is decremented
    // locally by a deposit or an offer; see the file docstring.
    this.bankGold.textContent = guildAmount(guild.gold);
    // Out is drawn only for the master, who is the only one who may take gold
    // out (`guild.cpp:1814`) *and* the only one it can be paid to
    // (`:1852-1860`). In is drawn for everybody, because any member may pay in
    // at any rank -- the original checks nothing but membership.
    this.bankWithdraw.hidden = !master;
    this.offerProgress.textContent =
      guild.next_exp === 0
        ? `${guildAmount(guild.exp)} (max)`
        : `${guildAmount(guild.exp)} / ${guildAmount(guild.next_exp)}`;
    this.offerMine.textContent = guildAmount(guild.your_offered);
    this.skillPower.textContent = `${guildAmount(guild.power)} / ${guildAmount(guild.max_power)}`;
    // `SetPercentage(power, max_power)` on the ani_image over the slot
    // (`uiguild.py:1655-1656`), minus the eight-pixel border the slot's own
    // raster draws.
    const filled = guild.max_power <= 0
      ? 0
      : Math.max(0, Math.min(1, guild.power / guild.max_power));
    this.skillGaugeFill.style.width = `${filled * (GUILD_GAUGE_WIDTH - 8)}px`;
    // The client only reflects the server's already-authoritative frame. It
    // never decrements points, power or cooldown optimistically.
    this.skillLearn.hidden = !master;
    const selected = guildSkillState(guild, this.selectedSkill);
    this.skillPoints.textContent = String(guild.skill_points);
    this.skillLevel.textContent = `${selected.level} / ${GUILD_SKILL_MAX_LEVEL}`;
    const row = GUILD_SKILLS.find(([vnum]) => vnum === this.selectedSkill);
    this.skillPick.title = guildSkillHoverText(row?.[1] ?? "", this.selectedSkill, selected.level);
    this.skillSlot.title = this.skillPick.title;
    this.skillSlot.classList.toggle("guild-skill-slot-unlearned", selected.level <= 0);
    this.skillSlot.replaceChildren(
      skillIconNode(row?.[2] ?? "", 0, this.root.ownerDocument)
        ?? this.root.ownerDocument.createTextNode(row?.[1] ?? ""),
    );
    this.skillLearn.disabled =
      guild.skill_points <= 0 || selected.level >= GUILD_SKILL_MAX_LEVEL;
    this.skillUse.hidden = !guild.may_use_skill;
    const passive = this.selectedSkill === 151;
    this.skillUse.disabled = passive || selected.level <= 0 || selected.ready > 0;
    this.skillUse.textContent = passive
      ? "Passive"
      : selected.ready > 0
      ? `Ready ${Math.ceil(selected.ready / 20)}s`
      : "Use";

    this.grades = guild.grades;
    this.fillRank();

    this.nameLabel.textContent = guild.name;
    this.count.textContent = `Lv ${guild.level} · ${guild.members.length}/${guild.max_members}`;

    // GetGuildExperienceSummary: every row's offer added up (PythonGuild.cpp:320).
    const offered = guild.members.reduce((sum, member) => sum + member.offer, 0);
    this.list.replaceChildren(
      ...guild.members.map((member: GuildMember, slot: number) => {
        const row = elem(doc, "div", "guild-member");
        // Faded, not hidden: an offline member is still a member, and a roster
        // that showed only who is here would be a party wearing a guild's name.
        if (!member.online) row.classList.add("guild-member-offline");

        const name = elem(doc, "span", "guild-member-name");
        if (member.grade === MASTER_GRADE) name.classList.add("guild-member-master");
        name.textContent = member.name;

        const rank = elem(doc, "span", "guild-member-rank");
        // The guild's own word for the rank, not a hardcoded one: rank names are
        // editable per guild (`guild.cpp:783-829`), so a client with a table of
        // its own would print the wrong word for every guild that renamed one.
        rank.textContent = member.grade_name;

        const level = elem(doc, "span", "guild-member-level", `Lv ${member.level}`);
        const job = elem(doc, "span", "guild-member-job", JOB_NAMES[member.job] ?? "?"); // JOB_NAME.get(job, "?") (uiguild.py:1604)
        const share = offered > 0 ? Math.trunc((member.offer / offered) * 100) : 0; // uiguild.py:1599-1601
        const offer = elem(doc, "span", "guild-member-offer", `${share}%`);

        row.append(name, rank, job, level, offer);

        if (master && member.grade !== MASTER_GRADE) {
          const general = doc.createElement("input");
          general.type = "checkbox";
          general.className = "guild-member-general";
          general.checked = member.is_general;
          general.setAttribute("aria-label", `General for ${member.name}`);
          general.addEventListener("change", () => {
            this.actions.onGeneral?.(slot, general.checked, guild.roster_revision);
          });
          row.append(general);
        }

        if (master && member.grade !== MASTER_GRADE) {
          rank.textContent = "";
          const pick = elem(doc, "select", "guild-select guild-member-grade");
          pick.setAttribute("aria-label", `Rank for ${member.name}`);
          for (let grade = 2; grade <= GRADE_COUNT; grade += 1) {
            const option = doc.createElement("option");
            option.value = String(grade);
            // The guild's own word for the rank, from the table the server
            // sent, so a renamed rank reads correctly here too.
            option.textContent =
              guild.grades[grade - 1]?.name ?? String(grade);
            option.selected = grade === member.grade;
            pick.append(option);
          }
          pick.addEventListener("change", () => {
            this.actions.onPromote?.(slot, Number(pick.value), guild.roster_revision);
          });

          const give = doc.createElement("button");
          give.type = "button";
          give.className = "guild-member-handover";
          give.textContent = this.handingOver === slot ? "Sure?" : "\u2605";
          give.title = `Make ${member.name} the guild master`;
          give.addEventListener("click", () => {
            // Two clicks, for the reason Leave and Disband are two buttons: the
            // server cannot tell a slip from an intention, and there is no
            // undo -- afterwards only the new master can hand it anywhere.
            if (this.handingOver !== slot) {
              this.handingOver = slot;
              give.textContent = "Sure?";
              return;
            }
            this.handingOver = -1;
            this.actions.onHandOver?.(slot, guild.roster_revision);
          });
          row.append(pick, give);
        }

        if (
          guild.may_expel &&
          member.grade !== MASTER_GRADE &&
          slot !== guild.your_slot
        ) {
          const expel = doc.createElement("button");
          expel.type = "button";
          expel.className = "guild-member-expel";
          expel.textContent = this.expelling === slot ? "Sure?" : "Expel";
          expel.title = `Expel ${member.name} from the guild`;
          expel.addEventListener("click", () => {
            if (this.expelling !== slot) {
              this.expelling = slot;
              expel.textContent = "Sure?";
              return;
            }
            this.expelling = -1;
            this.actions.onExpel?.(slot, guild.roster_revision);
          });
          row.append(expel);
        }
        return row;
      }),
    );

    // One of the two, never both, and never neither: see the file docstring.
    // Shown by rank rather than enforced by it -- the server refuses the wrong
    // one regardless, and a control that vanished would be a rule nobody sees.
    this.leave.hidden = master;
    this.disband.hidden = !master;
  }

  /** Draw only the server's durable rows; posting never mutates this list. */
  updateComments(frame: GuildComments): void {
    const doc = this.root.ownerDocument;
    this.commentList.replaceChildren(
      ...frame.comments.map((comment: GuildComment, slot: number) => {
        const row = elem(doc, "div", "guild-hint guild-comment");
        const author = doc.createElement("strong");
        author.textContent = `${comment.name}: `;
        const text = doc.createElement("span");
        text.textContent = comment.content;
        const remove = doc.createElement("button");
        remove.type = "button";
        remove.className = "guild-button guild-comment-delete";
        remove.textContent = "×";
        remove.title = "Delete message";
        remove.addEventListener("click", () =>
          this.actions.onDeleteComment?.(slot, frame.board_revision),
        );
        row.append(author, text, remove);
        return row;
      }),
    );
  }

  private submitComment(): void {
    const content = this.commentInput.value;
    if (content) this.actions.onPostComment?.(content);
  }

  /** Draw only server descriptions; its value is an opaque candidate token. */
  updateWar(menu: GuildWarMenu, arena: GuildWarArena): void {
    const doc = this.root.ownerDocument;
    this.warChoiceTokens = menu.choices.map((choice) => choice.token);
    this.warChoiceIsAccept = menu.choices.map((choice) => choice.name.startsWith("Accept "));
    this.warChoices.replaceChildren(...menu.choices.map((choice) => {
      const option = doc.createElement("option");
      option.value = String(choice.token);
      option.textContent = `${choice.opponent} · ${choice.mode} — ${choice.summary}`;
      return option;
    }));
    this.warChoices.disabled = menu.choices.length === 0;
    this.updateWarButton();
    this.warBoard.textContent = arena.active
      ? `${arena.mode}: ${arena.objective} ${arena.your_score} — ${arena.opponent_score} vs ${arena.opponent}`
      : "No active guild war.";
    // `RefreshGuildInfoPage` walks all six slots and writes the empty text into
    // the ones with no name, which is also what `OnEndGuildWar` does to the one
    // whose war just ended (`uiguild.py:1497-1505,1967-1981`).
    const boxes = [...this.enemyGuilds.children] as HTMLElement[];
    for (const [slot, box] of boxes.entries()) {
      const enemy = arena.enemies[slot];
      box.textContent = enemy === undefined ? ENEMY_GUILD_EMPTY : enemy.name;
      if (enemy === undefined) delete box.dataset.guildId;
      else box.dataset.guildId = String(enemy.guild_id);
    }
  }

  private updateWarButton(): void {
    const incoming = this.warChoiceIsAccept[this.warChoices.selectedIndex] === true;
    this.warButton.textContent = incoming
      ? "Accept"
      : "Challenge";
    this.warRefuseButton.hidden = !incoming;
    this.warRefuseButton.disabled = !incoming;
  }

  invited(invitation: GuildInvitation): void {
    if (invitation.token === 0) {
      this.drawn = null;
      this.prompt.hidden = true;
      return;
    }
    this.drawn = invitation;
    const who = invitation.inviter_name || "Somebody";
    const guild = invitation.guild_name || "a guild";
    // Both names, because they answer different questions: the player is
    // agreeing to join a *guild*, and the person asking is merely whoever holds
    // the permission to ask.
    this.promptText.textContent = `${who} invites you to ${guild}${formatInvitationExpiry(invitation.seconds)}.`;
    this.setAnswerable(true);
    this.prompt.hidden = false;
  }

  private submitFound(): void {
    const name = this.foundName.value.trim();
    if (name.length === 0) return;
    this.foundingRequested = false;
    if (this.currentGuild !== null) this.update(this.currentGuild);
    this.actions.onFound?.(name);
  }

  private answer(accept: boolean): void {
    const drawn = this.drawn;
    // The token of what is on screen. Never the connection's current
    // invitation, and never a re-read: if a second invitation has arrived since
    // this one was drawn, the server will refuse this token, and refusing is
    // the correct outcome -- the player agreed to what they read.
    if (drawn === null || drawn.token === this.answered) return;
    this.answered = drawn.token;
    this.setAnswerable(false);
    this.actions.onAnswer?.(drawn.token, accept);
  }

  private setAnswerable(can: boolean): void {
    this.accept.disabled = !can;
    this.decline.disabled = !can;
  }

  // ---------------------------------------------- ranks, bank, contribution

  /** A number field that does not let its own typing reach the window, for
   *  `foundName`'s reason. `allowNegative` skips the floor of zero every
   *  other field here gets -- world coordinates and a rotation may be
   *  negative, unlike gold, experience or a headcount. */
  private number(
    doc: Document,
    className: string,
    label: string,
    allowNegative = false,
  ): HTMLInputElement {
    const input = doc.createElement("input");
    input.type = "number";
    if (!allowNegative) input.min = "0";
    input.className = `guild-number ${className}`;
    input.placeholder = label;
    input.setAttribute("aria-label", label);
    input.autocomplete = "off";
    input.addEventListener("keydown", (event) => event.stopPropagation());
    return input;
  }

  private button(
    doc: Document,
    className: string,
    text: string,
    press: () => void,
  ): HTMLButtonElement {
    const button = doc.createElement("button");
    button.type = "button";
    button.className = `guild-button ${className}`;
    button.textContent = text;
    button.addEventListener("click", press);
    return button;
  }

  private fillRank(): void {
    const grade = Number(this.rankPick.value);
    const row: GuildGrade | undefined = this.grades[grade - 1];
    this.rankName.value = row?.name ?? "";
    for (const col of GRADE_AUTH_COLUMNS) {
      const box = this.rankAuth[col.field];
      if (box === undefined) continue;
      box.checked = gradeAuthValue(row, col.field);
    }
  }

  private submitRank(): void {
    const name = this.rankName.value.trim();
    if (name.length === 0) return;
    // The panel sends the full state of all four authority columns
    // (`guildwindow_gradepage.py:23-38`); the server sets exactly them.
    this.actions.onRank?.(
      Number(this.rankPick.value),
      name,
      this.rankAuth["may_invite"]?.checked ?? false,
      this.rankAuth["may_kick"]?.checked ?? false,
      this.rankAuth["may_notice"]?.checked ?? false,
      this.rankAuth["may_skill"]?.checked ?? false,
    );
  }

  private submitBank(withdraw: boolean): void {
    const amount = Math.floor(Number(this.bankAmount.value));
    if (!Number.isFinite(amount) || amount <= 0) return;
    this.actions.onBank?.(amount, withdraw);
  }

  private submitOffer(): void {
    const amount = Math.floor(Number(this.offerAmount.value));
    if (!Number.isFinite(amount) || amount <= 0) return;
    this.actions.onOffer?.(amount);
  }

  private submitCharge(): void {
    const amount = Math.floor(Number(this.skillChargeAmount.value));
    if (!Number.isFinite(amount) || amount <= 0) return;
    this.actions.onChargePower?.(amount);
  }
}
